import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCountry } from '../contexts/CountryContext';
import { useOfflineQueue } from '../hooks/useOfflineQueue';
import { refApi, signalApi, formatApiError } from '../lib/api';
import { SIGNAL_PRIORITIES, SIGNAL_STATUSES } from '../lib/countryConfig';

function Screen({ active, children, testId }) {
  return <div className={`flex-1 overflow-y-auto ${active ? '' : 'hidden'}`} data-testid={testId}>{children}</div>;
}

export default function Agent() {
  const navigate = useNavigate();
  const { profile, user, signOut } = useAuth();
  const { country, countryCode } = useCountry();
  const { queue, queueSize, isOnline, syncing, enqueue, sync } = useOfflineQueue();

  const [screen, setScreen] = useState('home');
  const [type, setType] = useState('EAU');
  const [titre, setTitre] = useState('');
  const [desc, setDesc] = useState('');
  const [priorite, setPriorite] = useState('normale');
  const [statut, setStatut] = useState('nouveau');
  const [gps, setGps] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [mySignals, setMySignals] = useState([]);
  const [communes, setCommunes] = useState([]);
  const [selectedCommuneId, setSelectedCommuneId] = useState(profile?.commune_id || '');
  const [communeNom, setCommuneNom] = useState('—');
  const [menuOpen, setMenuOpen] = useState(false);

  const loadMySignals = async () => {
    try {
      const { data } = await signalApi.list({ country_mode: countryCode, limit: 50 });
      setMySignals(data || []);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  useEffect(() => {
    (async () => {
      const { data: comList } = await refApi.communes(countryCode);
      setCommunes(comList || []);
      if (profile?.commune_id) {
        const c = comList.find((c) => c.id === profile.commune_id);
        setCommuneNom(c?.nom || '—');
        setSelectedCommuneId(profile.commune_id);
      } else if (comList?.length) {
        setSelectedCommuneId(comList[0].id);
        setCommuneNom(comList[0].nom);
      }
      await loadMySignals();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.commune_id, countryCode]);

  const greet = () => {
    const h = new Date().getHours();
    return h < 12 ? 'Bonjour' : h < 18 ? 'Bon après-midi' : 'Bonsoir';
  };

  const startSignal = (t) => {
    setType(t);
    setTitre('');
    setDesc('');
    setPriorite('normale');
    setStatut('nouveau');
    setGps(null);
    setError('');
    setSuccess('');
    setScreen('form');
  };

  const getGPS = () => {
    if (!navigator.geolocation) {
      setError('GPS non disponible');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => setGps({ lat: country.map.cityCenter[0], lng: country.map.cityCenter[1], approx: true }),
      { timeout: 8000 }
    );
  };

  const submit = async (e) => {
    e?.preventDefault();
    setError('');
    if (!titre.trim()) {
      setError('Le titre est obligatoire.');
      return;
    }
    setSubmitting(true);
    const payload = {
      type,
      titre: titre.trim(),
      description: desc,
      statut,
      priorite,
      latitude: gps?.lat ?? null,
      longitude: gps?.lng ?? null,
      commune_id: selectedCommuneId || profile?.commune_id || null,
      country_mode: countryCode,
    };

    if (!isOnline) {
      enqueue(payload);
      setSuccess('📵 Sauvegardé hors ligne — sera synchronisé.');
      setSubmitting(false);
      setTimeout(() => setScreen('home'), 1500);
      return;
    }

    try {
      const { data } = await signalApi.create(payload);
      setSuccess(`✅ Signalement envoyé (id: ${(data.id || '').slice(0, 8)}…)`);
      await loadMySignals();
    } catch (err) {
      enqueue(payload);
      setSuccess('⚠️ Sauvegardé localement. Sera synchronisé.');
      setError(formatApiError(err));
    }
    setSubmitting(false);
    setTimeout(() => setScreen('home'), 1800);
  };

  const handleLogout = async () => {
    setMenuOpen(false);
    await signOut();
    window.location.href = '/login';
  };

  const todayCount = mySignals.filter((s) => s.created_at?.startsWith(new Date().toISOString().slice(0, 10))).length;

  return (
    <div className="flex flex-col h-screen bg-[#F4F6F9]" style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }} data-testid="agent-page">
      <div className="h-[54px] flex items-center justify-between px-4 flex-shrink-0" style={{ background: country.palette.primary }}>
        <div className="flex items-center gap-2 text-white">
          <a href="/" className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-white/30 text-xs hover:bg-white/20 transition" style={{ background: 'rgba(255,255,255,.12)' }} data-testid="agent-back-dashboard">
            <span className="text-sm leading-none">←</span>
            <span className="hidden sm:inline">Dashboard</span>
          </a>
          <span className="text-base">{country.code === 'quebec' ? '🍁' : '🌍'}</span>
          <span className="text-sm font-semibold">{country.brand}</span>
          <span className="text-[11px] px-2 py-0.5 rounded-full" style={{ color: country.palette.light, background: 'rgba(255,255,255,.1)' }}>{communeNom}</span>
        </div>
        <div className="flex items-center gap-3 text-white">
          <div className="w-2 h-2 rounded-full" style={{ background: isOnline ? '#40916C' : '#C62828' }} title={isOnline ? 'En ligne' : 'Hors ligne'} data-testid="agent-sync-dot" />
          <div className="relative">
            <button onClick={(e) => { e.stopPropagation(); setMenuOpen((v) => !v); }} className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-semibold border-2 border-white/30" style={{ background: country.palette.primaryHover }} data-testid="agent-avatar">
              {(profile?.full_name || 'A').slice(0, 2).toUpperCase()}
            </button>
            {menuOpen && (
              <div className="absolute right-0 mt-2 w-44 bg-white text-gray-900 rounded-xl shadow-xl overflow-hidden z-50">
                <div className="px-4 py-3 text-xs font-semibold border-b border-gray-100">{profile?.full_name || user?.email}</div>
                <button onClick={handleLogout} className="w-full text-left px-4 py-3 text-sm text-red-600 hover:bg-red-50" data-testid="agent-logout">⬅️ Déconnexion</button>
              </div>
            )}
          </div>
        </div>
      </div>

      {!isOnline && <div className="bg-red-600 text-white text-center py-1.5 text-xs flex-shrink-0" data-testid="agent-offline-banner">📵 Hors ligne — Les signalements seront synchronisés</div>}
      {queueSize > 0 && (
        <div className="bg-yellow-50 border-b border-yellow-200 px-4 py-2 flex items-center justify-between text-xs text-yellow-800 flex-shrink-0" data-testid="agent-queue-banner">
          <span>⏳ {queueSize} signalement(s) en attente</span>
          {isOnline && <button onClick={sync} disabled={syncing} className="px-3 py-1 rounded-lg border border-yellow-700 text-yellow-800 hover:bg-yellow-100 disabled:opacity-50" data-testid="agent-sync-now">{syncing ? 'Sync…' : 'Synchroniser'}</button>}
        </div>
      )}

      <Screen active={screen === 'home'} testId="agent-screen-home">
        <div className="px-4 py-5 text-white" style={{ background: country.palette.primary }}>
          <div className="text-lg font-semibold">{greet()} !</div>
          <div className="text-xs mt-1" style={{ color: country.palette.light }}>Signalement rapide — Sélectionnez le type d'infrastructure</div>
        </div>
        <div className="grid grid-cols-2 gap-2.5 p-4">
          <Link to="/carte?focus=today" className="bg-white rounded-xl border border-gray-200 px-3 py-3.5 text-center hover:border-blue-500 hover:bg-blue-50 transition group" data-testid="agent-stat-today">
            <div className="text-2xl font-semibold">{todayCount}</div>
            <div className="text-[11px] text-gray-500 mt-0.5">Signalements aujourd'hui</div>
            <div className="text-[10px] font-semibold mt-1.5 flex items-center justify-center gap-1 opacity-80 group-hover:opacity-100" style={{ color: country.palette.primaryHover }}>🗺️ Voir sur la carte →</div>
          </Link>
          <Link to="/carte" className="bg-white rounded-xl border border-gray-200 px-3 py-3.5 text-center hover:border-orange-500 hover:bg-orange-50 transition group" data-testid="agent-stat-queue">
            <div className="text-2xl font-semibold" style={{ color: '#E65100' }}>{queueSize}</div>
            <div className="text-[11px] text-gray-500 mt-0.5">En attente sync</div>
            <div className="text-[10px] font-semibold mt-1.5 flex items-center justify-center gap-1 opacity-80 group-hover:opacity-100 text-orange-700">🗺️ Voir sur la carte →</div>
          </Link>
        </div>
        <div className="grid grid-cols-3 gap-2.5 px-4 pb-4">
          {country.infrastructureTypes.map((t) => (
            <button key={t.code} onClick={() => startSignal(t.code)} className="bg-white border border-gray-200 rounded-xl py-4 px-2 hover:border-blue-500 hover:bg-blue-50 transition text-center" data-testid={`agent-type-${t.code}`}>
              <div className="text-2xl mb-1.5">{t.icon}</div>
              <div className="text-xs font-medium">{t.name}</div>
            </button>
          ))}
        </div>
      </Screen>

      <Screen active={screen === 'form'} testId="agent-screen-form">
        <form className="p-4 space-y-3.5" onSubmit={submit}>
          <div className="text-base font-semibold flex items-center justify-between">
            Signalement — {country.infrastructureTypes.find((t) => t.code === type)?.name}
            <button type="button" onClick={() => setScreen('home')} className="text-xs text-gray-500" data-testid="agent-form-back">← Retour</button>
          </div>
          {error && <div className="bg-red-50 text-red-700 rounded-lg px-3 py-2 text-sm" data-testid="agent-form-error">{error}</div>}
          {success && <div className="bg-green-50 text-green-800 rounded-lg px-3 py-2 text-sm" data-testid="agent-form-success">{success}</div>}
          <div>
            <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Titre *</label>
            <input type="text" value={titre} onChange={(e) => setTitre(e.target.value)} placeholder="Ex: Fuite d'eau rue Sainte-Catherine" className="w-full px-3 py-3 rounded-lg border-2 border-gray-200 text-sm outline-none focus:border-blue-600" data-testid="agent-form-titre" required />
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">{country.labels.commune}</label>
            <select value={selectedCommuneId} onChange={(e) => setSelectedCommuneId(e.target.value)} className="w-full px-3 py-3 rounded-lg border-2 border-gray-200 text-sm outline-none focus:border-blue-600" data-testid="agent-form-commune">
              {communes.map((c) => (
                <option key={c.id} value={c.id}>{c.nom}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Priorité</label>
              <select value={priorite} onChange={(e) => setPriorite(e.target.value)} className="w-full px-3 py-3 rounded-lg border-2 border-gray-200 text-sm outline-none focus:border-blue-600" data-testid="agent-form-priorite">
                {SIGNAL_PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Statut</label>
              <select value={statut} onChange={(e) => setStatut(e.target.value)} className="w-full px-3 py-3 rounded-lg border-2 border-gray-200 text-sm outline-none focus:border-blue-600" data-testid="agent-form-statut">
                {SIGNAL_STATUSES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Description</label>
            <textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={3} className="w-full px-3 py-3 rounded-lg border-2 border-gray-200 text-sm outline-none focus:border-blue-600 resize-y" data-testid="agent-form-desc" />
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Localisation</label>
            <button type="button" onClick={getGPS} className="w-full py-3 rounded-lg border-2 border-blue-200 bg-blue-50 text-sm font-semibold text-blue-900 hover:bg-blue-100" data-testid="agent-form-gps">
              {gps ? `✅ ${gps.lat.toFixed(5)}, ${gps.lng.toFixed(5)} ${gps.approx ? '(approx)' : ''}` : '📍 Capturer ma position GPS'}
            </button>
          </div>
          <button type="submit" disabled={submitting} className="w-full py-3.5 rounded-xl text-white font-bold text-sm transition disabled:opacity-50" style={{ background: country.palette.primary }} data-testid="agent-form-submit">
            {submitting ? 'Envoi…' : isOnline ? 'Envoyer le signalement' : 'Sauvegarder hors ligne'}
          </button>
        </form>
      </Screen>

      <Screen active={screen === 'list'} testId="agent-screen-list">
        <div className="p-4 space-y-2">
          {queue.map((q, i) => (
            <div key={`q-${i}`} className="bg-yellow-50 border border-yellow-200 rounded-xl p-3.5">
              <div className="text-sm font-medium">{q.titre} <span className="text-[10px] text-orange-700">⏳ En attente</span></div>
              <div className="text-xs text-gray-600 mt-1">{q.type} · {q.priorite}</div>
            </div>
          ))}
          {mySignals.length === 0 && queue.length === 0 && <div className="text-center text-sm text-gray-500 py-10">Aucun signalement</div>}
          {mySignals.map((s) => {
            const p = SIGNAL_PRIORITIES.find((x) => x.value === s.priorite);
            return (
              <div key={s.id} className="bg-white rounded-xl border border-gray-200 p-3.5" data-testid={`agent-list-item-${s.id}`}>
                <div className="text-sm font-medium">{s.titre}</div>
                <div className="text-xs text-gray-600 mt-1 flex items-center gap-2">
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: '#f5f5f5', color: p?.color || '#666' }}>{p?.label || s.priorite}</span>
                  <span className="text-[11px]">{new Date(s.created_at).toLocaleString(country.labels.locale)}</span>
                  {s.commune_nom && <span className="text-[11px] text-gray-500">· {s.commune_nom}</span>}
                </div>
              </div>
            );
          })}
        </div>
      </Screen>

      <nav className="h-[60px] bg-white border-t border-gray-200 flex flex-shrink-0">
        <button onClick={() => setScreen('home')} className={`flex-1 flex flex-col items-center justify-center gap-0.5 ${screen === 'home' ? 'text-[#002855]' : 'text-gray-500'}`} data-testid="agent-nav-home">
          <span className="text-xl">🏠</span>
          <span className="text-[10px] font-medium">Accueil</span>
        </button>
        <button onClick={() => setScreen('list')} className={`flex-1 flex flex-col items-center justify-center gap-0.5 ${screen === 'list' ? 'text-[#002855]' : 'text-gray-500'}`} data-testid="agent-nav-list">
          <span className="text-xl">📋</span>
          <span className="text-[10px] font-medium">Mes signalements</span>
        </button>
      </nav>
    </div>
  );
}
